import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { useUser } from '../../context/UserContext';
import { motion } from 'framer-motion';
import Pagination from "@mui/material/Pagination";
import Stack from "@mui/material/Stack";
import { IoEyeOutline } from 'react-icons/io5';
import { useSearchParams } from 'react-router';
import api from '../../utils/api';

function LeaveReports() {

  const [leaveReports, setLeaveReports] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [requestsPerPage] = useState(8);
  const [apiLoading, setApiLoading] = useState(false);
  const { currentUser, loading } = useUser();
  const baseUrl = process.env.REACT_APP_API_URL;
  const [searchParams] = useSearchParams();
  const [selectedReport, setSelectedReport] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);

  // Filters
  const [leaveType, setLeaveType] = useState("ALL");
  const [status, setStatus] = useState("APPROVED");

  const fetchLeaveReports = async (page = 1, size = requestsPerPage) => {
    setApiLoading(true);
    try {
      const searchTerm = searchParams.get("search") || "";

      const response = await api.get(`/api/leaves/leave-reports`, {
        params: {
          page: page - 1,
          size,
          search: searchTerm,
          leaveType: leaveType === "ALL" ? null : leaveType,
          status: status === "ALL" ? null : status,
        }
      });

      const data = response.data;

      const mappedData = data.content.map(req => ({
        id: req.leaveRequestId,
        employeeName: req.employeeName,
        startDate: req.leaveStartDate,
        endDate: req.leaveEndDate,
        startDateTime: req.startDateTime,
        endDateTime: req.endDateTime,
        type: req.leaveType,
        status: req.leaveStatus,
        reason: req.reason,
        duration: req.duration,
        approverComment: req.approverComment
      }));

      setLeaveReports(mappedData);
      setTotalPages(data.totalPages);
      setCurrentPage(data.currentPage + 1); // backend is 0-based
    } catch (error) {
      console.error('Failed to fetch leave reports:', error);
    } finally {
      setApiLoading(false);
    }
  };

  useEffect(() => {
    fetchLeaveReports(currentPage);
  }, [currentPage]);


  useEffect(() => {
    setCurrentPage(1); // Reset to first page
    fetchLeaveReports(1);
  }, [searchParams, leaveType, status]);
  

  const handleView = (request) => {
    setSelectedReport(request);
    setShowViewModal(true);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const [year, month, day] = dateStr.split("-");
    return `${day}-${month}-${year}`;
  };

  if (loading || !currentUser) {
    return (
      <div className="p-4 sm:p-6 w-full mx-auto">
        <div className="flex justify-between items-center mb-6">
          <div className="h-8 w-48 bg-slate-200 rounded animate-pulse"></div>
          <div className="h-10 w-32 bg-slate-200 rounded-lg animate-pulse"></div>
        </div>
        <div className="hidden sm:block bg-white rounded-2xl shadow-md overflow-hidden">
          <div className="h-[400px] flex items-center justify-center">
            <svg className="animate-spin h-8 w-8 text-slate-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
            </svg>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="sm:p-6 sm:mt-auto mt-8 w-full mx-auto">
      <motion.div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6" initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}>
        <h2 className="text-xl xs:text-2xl font-bold">Leave Reports</h2>

        <div className="flex flex-wrap items-center gap-4">
          <div>
            <label className="text-sm text-slate-600 mr-2">Leave Type:</label>
            <select
              value={leaveType}
              onChange={(e) => setLeaveType(e.target.value)}
              className="border rounded px-3 py-1.5 text-sm"
            >
              <option value="ALL">All</option>
              <option value="Annual Leave">Annual Leave</option>
              <option value="Sick Leave">Sick Leave</option>
              <option value="Com Off Leave">Com Off Leave</option>
              <option value="Emergency Leave">Emergency Leave</option>
              <option value="Menstrual Leave">Menstrual Leave</option>
            </select>
          </div>

          <div>
            <label className="text-sm text-slate-600 mr-2">Status:</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="border rounded px-3 py-1.5 text-sm"
            >
              <option value="ALL">All</option>
              <option value="APPROVED">Approved</option>
              <option value="PENDING">Pending</option>
              <option value="REJECTED">Rejected</option>
              <option value="CANCELLED">Cancelled</option>
            </select>
          </div>
        </div>
      </motion.div>

      <motion.div className="hidden sm:block bg-white rounded-2xl shadow-md overflow-hidden" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="border-b border-slate-100">
              <tr>
                <th className="px-6 py-4 font-semibold text-slate-600 text-left">Sl. No</th>
                <th className="px-6 py-4 font-semibold text-slate-600 text-left">Employee</th>
                <th className="px-6 py-4 font-semibold text-slate-600 text-left">Start Date & Time</th>
                <th className="px-6 py-4 font-semibold text-slate-600 text-left">End Date & Time</th>
                <th className="px-6 py-4 font-semibold text-slate-600 text-left">Type</th>
                <th className="px-6 py-4 font-semibold text-slate-600 text-center">Status</th>
                <th className="px-6 py-4 font-semibold text-slate-600 text-center">View</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {apiLoading ? (
                <tr>
                  <td colSpan="7" className="px-6 py-8 text-center">
                    <div className="flex justify-center items-center">
                      <svg className="animate-spin h-8 w-8 text-slate-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
                      </svg>
                    </div>
                  </td>
                </tr>
              ) : leaveReports.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-8 text-center text-slate-500">No leave requests found.</td>
                </tr>
              ) : (
                leaveReports.map((request, index) => (
                  <tr key={request.id} className="hover:bg-slate-50/50 transition-colors duration-150">
                    <td className="px-6 py-4 text-slate-600">{(currentPage - 1) * requestsPerPage + index + 1}</td>
                    <td className="px-6 py-4"><div className="font-medium text-slate-700">{request.employeeName}</div></td>
                    <td className="px-6 py-4 text-slate-600">{formatDate(request.startDate)} ({request.startDateTime})</td>
                    <td className="px-6 py-4 text-slate-600">{formatDate(request.endDate)} ({request.endDateTime})</td>
                    <td className="px-6 py-4 text-slate-600">{request.type}</td>
                    <td className="px-6 py-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      request.status === "APPROVED" 
                        ? "bg-green-100 text-green-800"
                        : request.status === "PENDING"
                        ? "bg-yellow-100 text-yellow-800"
                        : "bg-red-100 text-red-800"
                    }`}>
                      {request.status}
                    </span>
                  </td>
                    <td className="px-6 py-4">
                      <div className="flex justify-center">
                        <motion.button whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }} onClick={() => handleView(request)} className="text-blue-600 hover:text-blue-800 transition-colors" title="View Details">
                          <IoEyeOutline className="text-xl" />
                        </motion.button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {/* Pagination */}
          {leaveReports.length > 0 && (
            <div className="px-6 py-4 border-t border-slate-100">
              <Stack spacing={2} className="flex justify-end">
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={(_, page) => setCurrentPage(page)}
                  variant="outlined"
                  shape="full"
                  size="large"
                  showFirstButton
                  showLastButton
                  sx={{
                    "& .MuiPaginationItem-root": {
                      color: "#475569",
                      borderColor: "#e2e8f0",
                    },
                    "& .Mui-selected": {
                      backgroundColor: "#475569 !important",
                      color: "#fff",
                    },
                  }}
                />
              </Stack>
            </div>
          )}
        </div>
      </motion.div>

      {/* View Modal */}
      {showViewModal && selectedReport && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-50 flex items-center justify-center" onClick={(e) => { if (e.target === e.currentTarget) setShowViewModal(false); }}>
          <motion.div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-xl mx-4 p-6 sm:p-8" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}>
            <button onClick={() => setShowViewModal(false)} className="absolute top-4 right-4 text-slate-400 hover:text-red-500 transition" aria-label="Close modal">✕</button>
            <div className="mb-6 text-center">
              <h2 className="text-2xl font-semibold text-slate-800">Leave Details</h2>
              <p className="text-sm text-slate-500">View full leave application summary</p>
            </div>
            <div className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <p className="text-sm text-slate-500">Employee Name</p>
                  <p className="font-medium text-slate-900">{selectedReport.employeeName}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Leave Type</p>
                  <p className="font-medium text-blue-600">{selectedReport.type}</p>
                </div>
              </div>
              <div>
                <p className="text-sm text-slate-500">Leave Duration</p>
                <p className="font-medium text-slate-900">{selectedReport.duration} {selectedReport.duration === 1 || selectedReport.duration === 0.5 ? "day" : "days"}</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <p className="text-sm text-slate-500">Start Date</p>
                  <p className="font-medium text-slate-900">{formatDate(selectedReport.startDate)} ({selectedReport.startDateTime})</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">End Date</p>
                  <p className="font-medium text-slate-900">{formatDate(selectedReport.endDate)} ({selectedReport.endDateTime})</p>
                </div>
              </div>
              <div>
                <p className="text-sm text-slate-500">Reason</p>
                <p className="font-medium text-slate-900 whitespace-pre-line">{selectedReport.reason}</p>
              </div>
            </div>
            <div className="mt-8 flex justify-center">
              <button onClick={() => setShowViewModal(false)} className="px-6 py-2 rounded-full bg-slate-800 text-white hover:bg-slate-700 transition">Close</button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}

export default LeaveReports;
